/**
 * APP-AI-1: Settings → AI client (superadmin). Keys are never sent or shown —
 * the server only reports whether each provider has one configured.
 */
import { adminFetch } from '@/lib/adminApi';

export type AiProviderId = 'anthropic' | 'openai';

export interface AiProviderStatus {
  id: AiProviderId;
  label: string;
  configured: boolean;
  /** The env variable the key was read from (name only), or null. */
  key_env?: string | null;
  /** The variable the provider expects — shown when no key is configured. */
  key_env_preferred?: string | null;
  /** Why the provider has a value but no usable key (variable names only). */
  key_problem?: string | null;
  model: string;
  model_source: 'override' | 'env' | 'default';
  default_model: string;
  last_success_at: number | null;
  last_error_at: number | null;
  last_error_kind: string | null;
  last_error_status: number | null;
  /** The vendor's error type, e.g. invalid_request_error. */
  last_error_type?: string | null;
  /** The vendor's own message — kept only for a failed Settings Test (no user data). */
  last_error_message?: string | null;
  breaker_open_until: number | null;
}

export interface AiSettingsResponse {
  /** AI_ASSIST_ENABLED === 'true' — assistance may run. */
  env_enabled: boolean;
  /** AI_ASSIST_ENABLED === 'false' — hard kill: even Test is blocked. */
  env_killed: boolean;
  enabled: boolean;
  primary: AiProviderId;
  backup: AiProviderId | null;
  store_ok: boolean;
  providers: AiProviderStatus[];
  features: { key: string; label: string; providers: string }[];
  privacy_note: string;
  /** The change was applied but its audit record could not be written. */
  audit_failed?: boolean;
}

export interface AiTestResult {
  ok: boolean;
  provider: AiProviderId;
  model: string;
  latency_ms?: number;
  kind?: string;
  status?: number | null;
  error_type?: string | null;
  error_message?: string | null;
  audit_failed?: boolean;
}

export function getAiSettings(): Promise<AiSettingsResponse> {
  return adminFetch<AiSettingsResponse>('/ai-settings');
}

export function updateAiSettings(patch: {
  enabled?: boolean;
  primary?: AiProviderId;
  backup?: AiProviderId | null;
}): Promise<AiSettingsResponse> {
  return adminFetch<AiSettingsResponse>('/ai-settings', { method: 'PUT', body: JSON.stringify(patch) });
}

export function testAiProvider(id: AiProviderId): Promise<AiTestResult> {
  return adminFetch<AiTestResult>(`/ai-settings/providers/${id}/test`, { method: 'POST' });
}

export function setAiModelOverride(id: AiProviderId, model: string | null): Promise<AiSettingsResponse> {
  return adminFetch<AiSettingsResponse>(`/ai-settings/providers/${id}/model`, {
    method: 'PUT',
    body: JSON.stringify({ model }),
  });
}

/**
 * Plain-language description of a provider failure. When the provider said WHY
 * (its error type and message), that is shown verbatim rather than a guess.
 */
export function describeAiFailure(
  kind: string | null | undefined,
  status?: number | null,
  errorType?: string | null,
  errorMessage?: string | null,
): string {
  const base = describeKind(kind, status);
  const why = [errorType, errorMessage].filter(Boolean).join(': ');
  return why ? `${base} — ${why}` : base;
}

function describeKind(kind: string | null | undefined, status?: number | null): string {
  switch (kind) {
    case 'auth': return 'Key rejected';
    case 'quota': return 'Out of credit or over the spending cap';
    case 'rate_limited': return 'Rate limited';
    case 'timeout': return 'Timed out';
    case 'network': return 'Could not reach the provider';
    case 'server': return `Provider error${status ? ` (${status})` : ''}`;
    case 'schema_invalid': return 'Answered in the wrong format';
    case 'bad_request': return `Request refused${status ? ` (${status})` : ''}`;
    case 'not_configured': return 'No API key configured';
    case 'breaker_open': return 'Paused after repeated failures';
    case 'disabled_env': return 'Blocked by the server kill switch';
    default: return kind ? kind : 'Unknown error';
  }
}
